import { useId } from 'react';
import { formatCompactNumber, formatNumber } from '../../utils/formatters';

const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = 260;
const PADDING = { top: 18, right: 18, bottom: 34, left: 18 };

function getMax(points = [], fallback = 1) {
  const highest = Math.max(...points.map((point) => Number(point.value || 0)), 0);
  return highest || fallback;
}

function createSmoothPath(points, width, height, maxValue) {
  if (!points.length) return { linePath: '', areaPath: '' };

  const chartWidth = width - PADDING.left - PADDING.right;
  const chartHeight = height - PADDING.top - PADDING.bottom;
  const stepX = points.length > 1 ? chartWidth / (points.length - 1) : 0;

  const coordinates = points.map((point, index) => {
    const x = PADDING.left + stepX * index;
    const y =
      PADDING.top +
      chartHeight -
      (Number(point.value || 0) / Math.max(maxValue, 1)) * chartHeight;
    return { x, y };
  });

  const linePath = coordinates.reduce((path, point, index, collection) => {
    if (index === 0) return `M ${point.x} ${point.y}`;
    const previous = collection[index - 1];
    const controlX = (previous.x + point.x) / 2;
    return `${path} C ${controlX} ${previous.y}, ${controlX} ${point.y}, ${point.x} ${point.y}`;
  }, '');

  const first = coordinates[0];
  const last = coordinates[coordinates.length - 1];
  const areaPath = `${linePath} L ${last.x} ${height - PADDING.bottom} L ${first.x} ${height - PADDING.bottom} Z`;

  return { linePath, areaPath, coordinates };
}

export function AreaTrendChart({
  points = [],
  color = '#FFCC00',
  accent = '#FF6D00',
  height = DEFAULT_HEIGHT,
  threshold = null,
  valueFormatter = formatCompactNumber,
}) {
  const gradientId = useId();
  const maxValue = getMax(points, threshold || 1);
  const { linePath, areaPath, coordinates = [] } = createSmoothPath(
    points,
    DEFAULT_WIDTH,
    height,
    maxValue
  );

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-[1.6rem] border border-[#3a3214] bg-[#0d0d0d] px-2 py-2">
        <svg viewBox={`0 0 ${DEFAULT_WIDTH} ${height}`} className="h-[18rem] w-full">
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity="0.32" />
              <stop offset="100%" stopColor={color} stopOpacity="0.06" />
            </linearGradient>
          </defs>

          {[0.25, 0.5, 0.75].map((line) => (
            <line
              key={line}
              x1={PADDING.left}
              x2={DEFAULT_WIDTH - PADDING.right}
              y1={PADDING.top + (height - PADDING.top - PADDING.bottom) * line}
              y2={PADDING.top + (height - PADDING.top - PADDING.bottom) * line}
              stroke="#2C2C2C"
              strokeWidth="1"
            />
          ))}

          {threshold != null ? (
            <line
              x1={PADDING.left}
              x2={DEFAULT_WIDTH - PADDING.right}
              y1={
                PADDING.top +
                (height - PADDING.top - PADDING.bottom) -
                (threshold / Math.max(maxValue, 1)) * (height - PADDING.top - PADDING.bottom)
              }
              y2={
                PADDING.top +
                (height - PADDING.top - PADDING.bottom) -
                (threshold / Math.max(maxValue, 1)) * (height - PADDING.top - PADDING.bottom)
              }
              stroke="#FF6D00"
              strokeDasharray="8 8"
              strokeWidth="2"
            />
          ) : null}

          <path d={areaPath} fill={`url(#${gradientId})`} />
          <path d={linePath} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" />

          {coordinates.map((point, index) => (
            <circle
              key={`${points[index]?.label || index}`}
              cx={point.x}
              cy={point.y}
              r={points[index]?.highlight ? 6 : 4}
              fill={points[index]?.highlight ? accent : color}
            />
          ))}
        </svg>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
        {points.map((point) => (
          <div key={point.label} className="rounded-[1.2rem] border border-[#2a2a2a] bg-black/40 px-3 py-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8A8A8A]">
              {point.label}
            </p>
            <p className="mt-2 text-sm font-bold text-white">{valueFormatter(point.value)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BarTrendChart({
  data = [],
  color = '#FFCC00',
  accent = '#FF6D00',
  height = 220,
  valueFormatter = formatCompactNumber,
}) {
  const maxValue = getMax(data, 1);
  const chartHeight = height - 42;

  return (
    <div className="space-y-4">
      <div className="flex h-[16rem] items-end gap-2 rounded-[1.6rem] border border-[#3a3214] bg-[#0d0d0d] px-4 py-4">
        {data.map((item, index) => {
          const barHeight = `${Math.max(14, (Number(item.value || 0) / maxValue) * chartHeight)}px`;
          const fill = item.highlight ? accent : color;

          return (
            <div key={item.label || index} className="flex flex-1 flex-col items-center justify-end gap-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8A8A8A]">
                {valueFormatter(item.value)}
              </div>
              <div
                className="w-full rounded-t-[1rem] transition-all"
                style={{
                  height: barHeight,
                  background: `linear-gradient(180deg, ${fill}, rgba(255, 255, 255, 0.04))`,
                  boxShadow: item.highlight ? '0 0 28px rgba(255, 109, 0, 0.22)' : 'none',
                }}
              />
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D7D7D7]">
                {item.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DonutGauge({
  value = 0,
  max = 100,
  size = 180,
  color = '#FFCC00',
  accent = '#FF6D00',
  label = '',
  subLabel = '',
  valueLabel = null,
}) {
  const normalizedMax = Math.max(Number(max || 0), 1);
  const normalizedValue = Math.min(Math.max(Number(value || 0), 0), normalizedMax);
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (normalizedValue / normalizedMax) * circumference;

  return (
    <div className="flex flex-col items-center justify-center rounded-[1.8rem] border border-[#3a3214] bg-[#0d0d0d] p-5 text-center">
      <svg viewBox="0 0 140 140" className="h-[11rem] w-[11rem]">
        <circle cx="70" cy="70" r={radius} fill="none" stroke="#2A2A2A" strokeWidth="14" />
        <circle
          cx="70"
          cy="70"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 70 70)"
        />
        <circle cx="70" cy="70" r="7" fill={accent} />
        <text x="70" y="68" textAnchor="middle" className="fill-white text-[24px] font-bold">
          {valueLabel || `${Math.round((normalizedValue / normalizedMax) * 100)}%`}
        </text>
        <text x="70" y="88" textAnchor="middle" className="fill-[#C9C1A6] text-[10px] uppercase tracking-[0.28em]">
          live
        </text>
      </svg>

      {label ? <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FEE8A8]">{label}</p> : null}
      {subLabel ? <p className="mt-2 text-sm leading-6 text-[#C7C7C7]">{subLabel}</p> : null}
    </div>
  );
}

export function HorizontalMeters({ items = [] }) {
  const maxValue = getMax(items, 1);

  return (
    <div className="space-y-4">
      {items.map((item) => {
        const width = `${(Number(item.value || 0) / maxValue) * 100}%`;
        return (
          <div key={item.label} className="rounded-[1.4rem] border border-[#2A2A2A] bg-black/40 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-bold text-white">{item.label}</p>
              <p className="text-sm font-bold text-[#FEE8A8]">{formatNumber(item.value)}</p>
            </div>
            <div className="mt-3 h-3 overflow-hidden rounded-full bg-[#2A2A2A]">
              <div
                className="h-full rounded-full"
                style={{
                  width,
                  background: `linear-gradient(90deg, ${item.color || '#FFCC00'}, ${item.accent || '#FF6D00'})`,
                }}
              />
            </div>
            {item.meta ? <p className="mt-3 text-xs uppercase tracking-[0.18em] text-[#8A8A8A]">{item.meta}</p> : null}
          </div>
        );
      })}
    </div>
  );
}
